import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { computeUnitEvenOwnership } from "@/lib/commission-ownership";
import { fetchAllPaginated } from "@/lib/supabase-paginate";
import type { SplitType } from "./use-project-detail";

/**
 * VIB-683 / VIB-1146: re-fordel ownership_pct jevnt PER SALGSENHET blant
 * percent-eiere, deretter jevnt mellom enhetens medlemmer — konsistent med
 * fan-outen i Nytt prosjekt. Legacy-eiere uten sales_unit_id teller som egen
 * enhet. Fixed-eiere er upåvirket og tar sin egen pott (fixed_amount håndteres
 * separat av calcCommissionSplits).
 */
async function rebalancePercentOwners(projectId: string) {
  const { data: owners, error } = await supabase
    .from("project_owners")
    .select("user_id, split_type, sales_unit_id")
    .eq("project_id", projectId);
  if (error || !owners) return;
  const splits = computeUnitEvenOwnership(owners);
  // Sekvensielt — Supabase JS har ikke transaksjonell bulk-update for ulike
  // verdier per rad. N er typisk 1-3, så roundtrip-kostnaden er ubetydelig.
  for (const [user_id, pct] of splits) {
    await supabase
      .from("project_owners")
      .update({ ownership_pct: pct })
      .eq("project_id", projectId)
      .eq("user_id", user_id);
  }
}

export interface ProjectOwner {
  project_id: string;
  user_id: string;
  sales_unit_id?: string | null;
  ownership_pct: number | null;
  split_type?: SplitType;
  fixed_amount?: number | null;
}

export function useProjectOwners() {
  return useQuery({
    queryKey: ["project-owners"],
    queryFn: (): Promise<ProjectOwner[]> => fetchAllPaginated<ProjectOwner>(
      "project_owners",
      "project_id, user_id, sales_unit_id, ownership_pct, split_type, fixed_amount",
      undefined,
      ["project_id", "user_id"],
    ),
    staleTime: 5 * 60 * 1000,
  });
}

/** Map project_id -> array of user_ids */
export function buildOwnersMap(owners: ProjectOwner[]): Map<string, string[]> {
  const map = new Map<string, string[]>();
  for (const o of owners) {
    if (!o.user_id) continue;
    const arr = map.get(o.project_id) ?? [];
    arr.push(o.user_id);
    map.set(o.project_id, arr);
  }
  return map;
}

/** Oppdater én eier sin splitt. Brukes av ProvisjonTab. */
export function useUpdateProjectOwner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      project_id: string;
      user_id: string;
      patch: {
        split_type?: SplitType;
        ownership_pct?: number | null;
        fixed_amount?: number | null;
      };
    }) => {
      const { error } = await supabase
        .from("project_owners")
        .update(input.patch)
        .eq("project_id", input.project_id)
        .eq("user_id", input.user_id);
      if (error) throw error;
      // VIB-683: hvis type endret seg fra/til 'percent', må vi rebalansere
      // de andre percent-eierne så summen blir 100.
      if (input.patch.split_type !== undefined) {
        await rebalancePercentOwners(input.project_id);
      }
    },
    onSuccess: async (_d, vars) => {
      await invalidateFor(qc, "project_owners", { projectId: vars.project_id });
    },
    onError: (err) => {
      toast.error(`Kunne ikke oppdatere splitt: ${(err as Error).message}`);
    },
  });
}

/**
 * VIB-1146: Legg til en SALGSENHET som prosjekteier. Fan-outer enhetens
 * medlemmer til én project_owners-rad hver med sales_unit_id satt, så
 * rebalanserer eierandelene jevnt per enhet (som Nytt prosjekt). Medlemmer som
 * allerede er eiere (f.eks. via en annen enhet) hoppes over for å unngå
 * duplikat — PK er (project_id, user_id).
 */
export function useAddSalesUnitOwners() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      project_id: string;
      sales_unit_id: string;
      member_user_ids: string[];
    }) => {
      if (input.member_user_ids.length === 0) {
        throw new Error("Selgergruppen har ingen medlemmer.");
      }
      // Filtrer bort medlemmer som allerede er eiere på prosjektet.
      const { data: existing, error: exErr } = await supabase
        .from("project_owners")
        .select("user_id")
        .eq("project_id", input.project_id);
      if (exErr) throw exErr;
      const existingIds = new Set((existing ?? []).map((o) => o.user_id));
      const newIds = input.member_user_ids.filter((id) => !existingIds.has(id));
      if (newIds.length === 0) {
        throw new Error("Alle medlemmene er allerede eiere på prosjektet.");
      }

      const rows = newIds.map((user_id) => ({
        project_id: input.project_id,
        user_id,
        sales_unit_id: input.sales_unit_id,
        ownership_pct: 0,
        split_type: "percent" as SplitType,
      }));
      const { error } = await supabase.from("project_owners").insert(rows);
      if (error) throw error;
      await rebalancePercentOwners(input.project_id);
    },
    onSuccess: async (_d, vars) => {
      await invalidateFor(qc, "project_owners", { projectId: vars.project_id });
    },
    onError: (err) => {
      toast.error(`Kunne ikke legge til selger: ${(err as Error).message}`);
    },
  });
}

/** Fjern en salgseier fra et prosjekt. VIB-683: rebalanserer percent-eiere
 *  etter delete så summen forblir 100. */
export function useDeleteProjectOwner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { project_id: string; user_id: string }) => {
      const { error } = await supabase
        .from("project_owners")
        .delete()
        .eq("project_id", input.project_id)
        .eq("user_id", input.user_id);
      if (error) throw error;
      await rebalancePercentOwners(input.project_id);
    },
    onSuccess: async (_d, vars) => {
      await invalidateFor(qc, "project_owners", { projectId: vars.project_id });
    },
    onError: (err) => {
      toast.error(`Kunne ikke fjerne eier: ${(err as Error).message}`);
    },
  });
}
